import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Chip, Button, Divider, CircularProgress,
  Stack, Avatar, IconButton, Tooltip, useTheme,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import CallIcon from '@mui/icons-material/Call';
import EmailIcon from '@mui/icons-material/Email';
import ForumIcon from '@mui/icons-material/Forum';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { marketingApi, followUpApi } from '../api/apiClient';
import PageShell from '../common/PageShell';
import EmptyState from '../common/EmptyState';

const colorFor = (name = '') => {
  const palette = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669', '#0891b2', '#4f46e5'];
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return palette[sum % palette.length];
};

const modeIcon = (mode = '') => {
  const m = mode.toLowerCase();
  if (m.includes('call') || m.includes('phone')) return <CallIcon fontSize="small" />;
  if (m.includes('email') || m.includes('mail')) return <EmailIcon fontSize="small" />;
  if (m.includes('meet') || m.includes('visit')) return <MeetingRoomIcon fontSize="small" />;
  return <ForumIcon fontSize="small" />;
};

const InfoRow = ({ icon, label, value, color = 'primary.main' }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ py: 1.25 }}>
    <Avatar
      sx={{
        bgcolor: color,
        width: 36,
        height: 36,
        flexShrink: 0,
        '& svg': { fontSize: 18 },
      }}
    >
      {icon}
    </Avatar>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}
      >
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word', mt: 0.25 }}>
        {value || '—'}
      </Typography>
    </Box>
  </Stack>
);

const MarketingView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const fetchedRef = useRef(false);
  const theme = useTheme();

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    marketingApi.getById(id).then((res) => setData(res.data));
    followUpApi
      .getByMarketing(id)
      .then((res) => setFollowUps(res.data || []))
      .catch(() => setFollowUps([]));
  }, [id]);

  if (!data) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const companyColor = colorFor(data.companyName);

  return (
    <PageShell
      icon={<StorefrontIcon />}
      title={data.companyName}
      subtitle="Marketing record details and follow-up history"
      breadcrumbs={[
        { label: 'Dashboard', to: '/' },
        { label: 'Marketing', to: '/marketing' },
        { label: data.companyName },
      ]}
      actions={
        <>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/marketing')}
            sx={{
              color: '#fff',
              borderColor: 'rgba(255,255,255,0.5)',
              '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
            }}
            variant="outlined"
          >
            Back
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/marketing/edit/${id}`)}
            sx={{
              bgcolor: '#fff',
              color: '#1e3a8a',
              fontWeight: 700,
              '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
            }}
          >
            Edit
          </Button>
        </>
      }
    >
      <Grid container spacing={3}>
        {/* LEFT: main info */}
        <Grid item xs={12} md={8}>
          <Stack spacing={3}>
            {/* Company card */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ mb: 2.5 }}
              >
                <Avatar
                  sx={{
                    bgcolor: companyColor,
                    width: 64,
                    height: 64,
                    fontSize: 26,
                    fontWeight: 700,
                    boxShadow: `0 8px 20px ${companyColor}44`,
                  }}
                >
                  {(data.companyName || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      sx={{ wordBreak: 'break-word', lineHeight: 1.3 }}
                    >
                      {data.companyName}
                    </Typography>
                    <Chip
                      label={data.active ? 'Active' : 'Inactive'}
                      color={data.active ? 'success' : 'default'}
                      size="small"
                      icon={data.active ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Marketing ID #{data.id}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 1 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<PhoneIcon />}
                    label="Contact Number"
                    value={data.contactNumber}
                    color="#2563eb"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InfoRow
                    icon={<PersonIcon />}
                    label="Sales Executive"
                    value={
                      data?.salesExecutiveName ? `${data.salesExecutiveName}`.trim() : 'Unassigned'
                    }
                    color="#7c3aed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoRow
                    icon={<LocationOnIcon />}
                    label="Address"
                    value={data.address}
                    color="#db2777"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Follow-ups */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'center' }}
                spacing={1.5}
                sx={{ mb: 2 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Avatar
                    sx={{
                      bgcolor: 'secondary.main',
                      width: 36,
                      height: 36,
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    <EventIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Follow-Ups
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {followUps.length} {followUps.length === 1 ? 'entry' : 'entries'}
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  startIcon={<AddIcon />}
                  variant="contained"
                  onClick={() => navigate(`/follow-ups/new?marketingId=${id}`)}
                  size="small"
                  sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                  Add Follow-Up
                </Button>
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {followUps.length === 0 ? (
                <EmptyState
                  icon={<EventIcon />}
                  title="No follow-ups yet"
                  subtitle="Schedule a call, email, or meeting to start tracking engagement."
                  actionLabel="Add Follow-Up"
                  onAction={() => navigate(`/follow-ups/new?marketingId=${id}`)}
                />
              ) : (
                <Stack spacing={1.5}>
                  {followUps.map((f) => (
                    <Paper
                      key={f.id}
                      elevation={0}
                      onClick={() => navigate(`/follow-ups/view/${f.id}`)}
                      sx={{
                        p: 1.75,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        transition: 'all .15s',
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: 'action.hover',
                          transform: 'translateX(3px)',
                        },
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        <Avatar
                          sx={{
                            bgcolor: 'secondary.light',
                            color: 'secondary.dark',
                            width: 40,
                            height: 40,
                            flexShrink: 0,
                          }}
                        >
                          {modeIcon(f.mode)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            spacing={1}
                          >
                            <Typography variant="body2" fontWeight={700}>
                              {f.mode || 'Follow-up'}
                            </Typography>
                            {f.followUpDate && (
                              <Chip
                                label={f.followUpDate}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 11, height: 22 }}
                              />
                            )}
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {f.description || 'No description provided.'}
                          </Typography>
                        </Box>
                        <Tooltip title="Open">
                          <IconButton size="small" sx={{ alignSelf: 'center' }}>
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Grid>

        {/* RIGHT: sidebar */}
        <Grid item xs={12} md={4}>
          <Stack spacing={3} sx={{ position: { md: 'sticky' }, top: { md: 24 } }}>
            {/* At a glance */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  mb: 2,
                }}
              >
                At a Glance
              </Typography>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={data.active ? 'Active' : 'Inactive'}
                    color={data.active ? 'success' : 'default'}
                    size="small"
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Follow-Ups
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    {followUps.length}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Executive
                  </Typography>
                  <Typography variant="body2" fontWeight={700} noWrap sx={{ maxWidth: 160 }}>
                    {data?.salesExecutiveName?.trim() || 'Unassigned'}
                  </Typography>
                </Stack>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Record ID
                  </Typography>
                  <Typography variant="body2" fontWeight={700}>
                    #{data.id}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Quick actions */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 3 },
                borderRadius: 2.5,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={700}
                sx={{
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: 'text.secondary',
                  fontSize: 11,
                  mb: 2,
                }}
              >
                Quick Actions
              </Typography>
              <Stack spacing={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/marketing/edit/${id}`)}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Edit Record
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(`/follow-ups/new?marketingId=${id}`)}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  New Follow-Up
                </Button>
                {data.contactNumber && (
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<CallIcon />}
                    href={`tel:${data.contactNumber}`}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    Call Contact
                  </Button>
                )}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/marketing')}
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Back to List
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </PageShell>
  );
};

export default MarketingView;